<script>
  import {
    Icon,
    CodeBracket,
    PlayCircle,
    PlusCircle,
    ExclamationCircle,
    BugAnt,
    QuestionMarkCircle,
    QueueList,
    CheckCircle,
    PauseCircle,
    TableCells,
    Document,
    DocumentCheck,
    LockOpen,
    LockClosed,
    Sparkles,
    ChatBubbleLeftRight,
    ArrowsPointingOut,
    ArrowsPointingIn,
  } from "svelte-hero-icons";
  import {
    toolbar,
    workflow,
    currentStep,
    execution,
    liveStore,
    filetree,
    features,
    ai,
  } from "./store";
  import {
    generateEvents,
    generateValidationIssues,
    simulateWorkflow,
  } from "$lib/Mock/mocks/automator";
  import { get, writable } from "$lib/utils/shim";
  import { onMount, onDestroy } from "svelte";

  import { tooltip } from "$lib/utils/tooltip";
  import { cx } from "$lib/utils/cx";
  import { persisted } from "$lib/utils/persisted-stores";
  import HelpModal from "$lib/Elements/HelpModal.svelte";
  import TraceDebug from "./Menubar/TraceDebug.svelte";
  import Execute from "./Menubar/Execute.svelte";
  import { assignStoresToConsole } from "$lib/shared_stores";

  export let hasActiveWorkflow = false;
  export let projectId = null;

  $: isValid = workflow.isValid;
  $: isReadonly = workflow.isReadonly;
  $: rootUser = features.rootUser;
  $: showRootRequestOptions = features.showRootRequestOptions;
  $: aiWorkflowAssistantEnabled = features.aiWorkflowAssistantEnabled;
  $: autoSaveEnabled = workflow.autoSaveEnabled;
  $: selectedWorkflow = filetree.selectedWorkflow;
  $: isAISidebarOpen = ai.isSidebarOpen;

  const additionalHelp = persisted("show_additional_help", true);
  const toggleAdditionalHelp = () => additionalHelp.update((value) => !value);
  const alwaysReadwriteIfPossible = persisted("always_readwrite", false);
  const autoRezoom = persisted("auto_rezoom", false);
  const toggleAutoRezoom = () => autoRezoom.update((value) => !value);
  const toggleDebug = () => (($toolbar.show_debug = !$toolbar.show_debug), assignStoresToConsole());
  const onClickCode = () => ($toolbar.show_code = !$toolbar.show_code);
  const onClickTable = () => ($toolbar.show_table = !$toolbar.show_table);

  function togglePropPane() {
    $toolbar.show_prop_pane = !$toolbar.show_prop_pane;
  }

  function toggleCodePane() {
    $toolbar.show_code_pane = !$toolbar.show_code_pane;
  }

  let isHelpOpen = false;
  const toggleHelp = () => (isHelpOpen = !isHelpOpen);
  const keyboardShortcuts = {
    Navigation: {
      "Move Up": "↑",
      "Move Down": "↓",
      "Move Right": "→",
      "Move Left": "←",
      "Enter a control flow block": "Enter",
      "Zoom In": "+",
      "Zoom Out": "-",
      "Reset View": "F",
    },
    Operation: {
      Copy: ["ctrl_or_meta", "C"],
      Paste: ["ctrl_or_meta", "V"],
      Duplicate: ["ctrl_or_meta", "D"],
      Cut: ["ctrl_or_meta", "X"],
      "Toggle Skip": ["ctrl_or_meta", "/"],
      Delete: "Delete",
      Undo: ["ctrl_or_meta", "Z"],
      Redo: ["ctrl_or_meta", "Shift", "Z"],
    },
    AI: {
      "Open AI Prompt": ["ctrl_or_meta", "K"],
    },
  };

  let apps = [];
  let devices = [];
  let regions = [];
  let availableSidecars = writable([]);
  let availableEntrypoints = writable([]);
  let selectedApp = null;
  let selectedDevice = null;
  let selectedBuild = null;
  let selectedRegion = "new_york_usa";
  let selectedStaticTeleport = false;
  let selectedSidecar = [];
  let selectedEntrypoint = null;

  async function onOpenDropdown() {
    const [targets, teleport] = await Promise.all([
      $liveStore.pushEvent("request_targets"),
      $liveStore.pushEvent("request_teleport_regions"),
    ]);

    apps = targets.apps
      .map((app) => ({
        value: app.id,
        label: app.name,
        buildsAllowed: app.builds_allowed,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));

    devices = targets.devices
      .map((device) => ({
        value: device.id,
        label: `${device.make} ${device.model}`,
        image: device.logo,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));

    regions = teleport.regions;

    if (selectedApp == null && apps.length > 0) {
      selectedApp = apps[0].value;
    }

    if (selectedDevice == null && devices.length > 0) {
      selectedDevice = devices[0].value;
    }

    await onDeviceChange();
  }

  async function onDeviceChange() {
    if ($showRootRequestOptions && selectedDevice) {
      const { sidecars } = await $liveStore.pushEvent("request_sidecars", {
        payload: { selectedDevice },
      });
      availableSidecars.update(() => sidecars);
    }
  }

  async function updateEntrypoints(onlyEntrypoints = true) {
    availableEntrypoints.set([]);

    const { entrypoints } = await $liveStore.pushEvent("request_entrypoints", {
      only_entrypoints: onlyEntrypoints,
    });
    availableEntrypoints.update(() =>
      entrypoints.map(({ id, name, meta }) => {
        const isLibrary = meta?.type === "library";
        return {
          value: id,
          label: isLibrary ? `📚 ${name}` : name,
          isLibrary,
        };
      }),
    );
  }

  function getCurrentWorkflow() {
    const currentWorkflow = get(workflow);
    if (!currentWorkflow || !currentWorkflow.sequence || currentWorkflow.sequence.length === 0) {
      console.warn("No workflow or empty sequence");
      return null;
    }
    return currentWorkflow;
  }

  function onSimulateWorkflow() {
    console.clear();
    const workflow = getCurrentWorkflow();
    if (workflow) {
      execution.clearAllStatuses();
      simulateWorkflow(generateEvents(workflow), (event) => execution.processEvent(event), 1000);
    }
  }

  function onAddExampleBlocks() {
    workflow.addBlock("open_app");
    workflow.addBlock("press_key");
    workflow.addBlock("close_app");
  }

  function onAddValidationErrors() {
    console.clear();
    execution.clearAllValidations();
    const workflow = getCurrentWorkflow();
    if (workflow) {
      simulateWorkflow(
        generateValidationIssues(workflow),
        (event) => execution.processEvent(event),
        10,
      );
    }
  }

  function handleKeydown(event) {
    // Ctrl/Cmd + K to open AI prompt (only for users with AI access)
    if ((event.ctrlKey || event.metaKey) && event.key === "k" && $aiWorkflowAssistantEnabled) {
      event.preventDefault();
      ai.openOverlay();
    }
  }

  onMount(() => {
    document.addEventListener("keydown", handleKeydown);
  });

  onDestroy(() => {
    document.removeEventListener("keydown", handleKeydown);
  });
</script>

<div class="flex items-center py-1 px-4 bg-surface border-b border-line-strong">
  <div class="flex space-x-2 items-center absolute">
    {#if $selectedWorkflow}
      <Icon
        src={$selectedWorkflow.meta?.type === "library" ? Document : DocumentCheck}
        class="w-4 h-4"
      />
      <div class="text-sm font-semibold">{$selectedWorkflow.name}</div>
    {/if}

    {#if $toolbar.show_debug}
      <div class="text-xs font-mono">
        <strong>{$isValid ? "valid" : "invalid"}</strong> | <span>{$currentStep}</span>
      </div>
      <button
        class="p-1 text-content hover:bg-surface-hover rounded-md flex items-center justify-center w-8 h-8"
        on:click={onAddExampleBlocks}
        title="Add random blocks"
      >
        <Icon src={PlusCircle} class="w-5 h-5" />
      </button>
      <button
        class="p-1 text-content hover:bg-surface-hover rounded-md flex items-center justify-center w-8 h-8"
        on:click={onSimulateWorkflow}
        title="Simulate Workflow Run"
      >
        <Icon src={PlayCircle} class="w-5 h-5" />
      </button>
      <button
        class="p-1 text-content hover:bg-surface-hover rounded-md flex items-center justify-center w-8 h-8"
        on:click={onAddValidationErrors}
        title="Add random validation errors"
      >
        <Icon src={ExclamationCircle} class="w-5 h-5" />
      </button>
    {/if}
  </div>

  <div class="grow" />

  <div class="flex space-x-4">
    <button
      class="p-1 text-content hover:bg-surface-hover rounded-md flex items-center justify-center w-8 h-8 disabled:opacity-20"
      class:bg-surface-hover={$toolbar.show_code}
      on:click={onClickCode}
      use:tooltip={{ placement: "bottom" }}
      aria-label="Toggle code view"
      disabled={!hasActiveWorkflow}
    >
      <Icon src={CodeBracket} class="w-5 h-5" />
    </button>

    <button
      class="p-1 text-content hover:bg-surface-hover rounded-md flex items-center justify-center w-8 h-8 disabled:opacity-20"
      class:bg-surface-hover={$toolbar.show_table}
      on:click={onClickTable}
      use:tooltip={{ placement: "bottom" }}
      aria-label="Toggle table view"
      disabled={!hasActiveWorkflow}
    >
      <Icon src={TableCells} class="w-5 h-5" />
    </button>

    <button
      class="p-1 text-content hover:bg-surface-hover rounded-md flex items-center justify-center w-8 h-8 disabled:opacity-20"
      on:click={toggleCodePane}
      disabled={$toolbar.show_code}
      use:tooltip={{ placement: "bottom" }}
      aria-label="Toggle code pane"
    >
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="-4 -4 104 108" class="w-5 h-5">
        <rect
          x="0"
          y="0"
          width="50"
          height="100"
          fill={$toolbar.show_code_pane ? "black" : "none"}
          stroke="black"
          stroke-width="8"
        />
        <rect x="50" y="0" width="50" height="100" fill="none" stroke="black" stroke-width="8" />
      </svg>
    </button>

    <button
      class="p-1 text-content hover:bg-surface-hover rounded-md flex items-center justify-center w-8 h-8 disabled:opacity-20"
      on:click={togglePropPane}
      disabled={$toolbar.show_code}
      use:tooltip={{ placement: "bottom" }}
      aria-label="Toggle properties pane"
    >
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="-4 -4 104 108" class="w-5 h-5">
        <rect
          x="50"
          y="0"
          width="50"
          height="100"
          fill={$toolbar.show_prop_pane ? "black" : "none"}
          stroke="black"
          stroke-width="8"
        />
        <rect x="0" y="0" width="50" height="100" fill="none" stroke="black" stroke-width="8" />
      </svg>
    </button>

    {#if $rootUser}
      <button
        class="p-1 text-content hover:bg-surface-hover rounded-md flex items-center justify-center w-8 h-8"
        on:click={toggleDebug}
        use:tooltip={{ placement: "bottom" }}
        aria-label="Toggle debug view"
      >
        <Icon src={BugAnt} class="w-5 h-5" />
      </button>
    {/if}

    <button
      on:click={toggleAdditionalHelp}
      class="p-1 text-content hover:bg-surface-hover rounded-md disabled:opacity-20"
      use:tooltip={{ placement: "bottom" }}
      aria-label="Toggle Additional Help"
    >
      <Icon src={QueueList} class="w-5 h-5" />
    </button>

    <button
      on:click={toggleHelp}
      class="p-1 text-content hover:bg-surface-hover rounded-md disabled:opacity-20"
      use:tooltip={{ placement: "bottom" }}
      aria-label="Show Help"
    >
      <Icon src={QuestionMarkCircle} class="w-5 h-5" />
    </button>

    {#if $aiWorkflowAssistantEnabled}
      <button
        on:click={() => ai.openOverlay()}
        class="p-1 text-content hover:bg-surface-hover rounded-md disabled:opacity-20"
        use:tooltip={{ placement: "bottom" }}
        aria-label="AI Prompt"
      >
        <Icon src={Sparkles} class="w-5 h-5" />
      </button>

      <button
        on:click={() => ($isAISidebarOpen ? ai.closeSidebar() : ai.openSidebar())}
        class="p-1 text-content hover:bg-surface-hover rounded-md disabled:opacity-20"
        class:bg-surface-hover={$isAISidebarOpen}
        use:tooltip={{ placement: "bottom" }}
        aria-label="Toggle AI Assistant Panel"
      >
        <Icon src={ChatBubbleLeftRight} class="w-5 h-5" />
      </button>
    {/if}

    <button
      on:click={toggleAutoRezoom}
      class="p-1 text-content hover:bg-surface-hover rounded-md disabled:opacity-20"
      class:text-green-800={$autoRezoom}
      use:tooltip={{ placement: "bottom" }}
      aria-label="Toggle Auto-Rezoom"
    >
      <Icon src={$autoRezoom ? ArrowsPointingIn : ArrowsPointingOut} class="w-5 h-5" />
    </button>

    <button
      on:click={() => workflow.toggleAutoSaveEnabled()}
      class="p-1 text-content hover:bg-surface-hover rounded-md disabled:opacity-20"
      class:text-green-800={$autoSaveEnabled}
      use:tooltip={{ placement: "bottom" }}
      aria-label="Toggle Auto-Save"
    >
      <Icon src={$autoSaveEnabled ? CheckCircle : PauseCircle} class="w-5 h-5" />
    </button>

    <button
      on:click={() => workflow.toggleReadonlyMode()}
      on:dblclick={() => workflow.lockReadwriteMode()}
      class={cx("p-1 text-content hover:bg-surface-hover rounded-md disabled:opacity-20", {
        "bg-indigo-100 hover:bg-indigo-200": !$isReadonly && $alwaysReadwriteIfPossible,
      })}
      class:text-green-800={$isReadonly}
      use:tooltip={{ placement: "bottom" }}
      aria-label="Single-click to toggle read-only mode. Double-click to lock/unlock read & write mode if you have permission to edit the workflow."
    >
      <Icon
        src={$isReadonly ? LockClosed : LockOpen}
        class={cx("w-5 h-5", { "text-indigo-600": $alwaysReadwriteIfPossible })}
      />
    </button>
  </div>

  <div class="grow" />

  <div class="flex items-center space-x-2 absolute right-4">
    <TraceDebug
      {apps}
      {devices}
      {regions}
      {availableSidecars}
      {availableEntrypoints}
      {projectId}
      bind:selectedApp
      bind:selectedDevice
      bind:selectedRegion
      bind:selectedStaticTeleport
      bind:selectedSidecar
      bind:selectedEntrypoint
      bind:selectedBuild
      on:open={onOpenDropdown}
      on:deviceChange={onDeviceChange}
      on:updateEntrypoints={(e) => updateEntrypoints(e.detail.onlyEntrypoints)}
    />
    {#if $showRootRequestOptions}
      <Execute
        {apps}
        {devices}
        {projectId}
        {availableEntrypoints}
        on:open={onOpenDropdown}
        on:updateEntrypoints={(e) => updateEntrypoints(e.detail.onlyEntrypoints)}
      />
    {/if}
  </div>
</div>

<HelpModal bind:isOpen={isHelpOpen} maxWidth="32rem" {keyboardShortcuts} />
